import * as XLSX from 'xlsx';
import { Half, halfYearMonths, toMonth } from './period';
import { sheetToAoaFullRange } from './readXlsx';
import { ChannelResult, EasyCheckMonthRow } from './types';

function num(v: any): number { return Number(v) || 0; }

// easyshop 원본 고정 컬럼: 0 매출일, 1 카드사명, 2 건수, 3 승인금액, 4 과세, 5 부가세, 6 비과세, 7 봉사료
const C = { date: 0, name: 1, taxable: 4, vat: 5, exempt: 6 };

function emptyRow(month: string): EasyCheckMonthRow {
  return { month, taxable: 0, exempt: 0, cashReceipt: { taxable: 0, exempt: 0 }, card: { taxable: 0, exempt: 0 } };
}

export function parseEasyCheckRows(aoa: any[][], year: number, half: Half): ChannelResult<EasyCheckMonthRow> {
  const target = halfYearMonths(year, half);
  const byMonth = new Map<string, EasyCheckMonthRow>();

  // easyshop 원본은 상단 제목/사업자정보 + 2행 헤더, 그 아래 일자별 데이터,
  // 이어서 소계·카드종류별 요약(신용카드/현금영수증/직불선불)·총계·설명문이 온다.
  // 데이터 행은 매출일(col0)이 날짜인 행뿐이라, 전 행을 훑되 날짜가 파싱되는 행만 집계한다.
  // (요약/총계/설명문 행은 col0 이 날짜가 아니므로 자연히 제외되어 이중집계되지 않음)
  for (let i = 0; i < aoa.length; i++) {
    const row = aoa[i];
    if (!row) continue;
    const rawDate = String(row[C.date] ?? '').trim();
    if (rawDate === '' || rawDate === '소계') continue;
    const month = toMonth(rawDate);
    if (!target.includes(month)) continue;

    // 검증 포인트: 과세 매출을 `과세+부가세`(공급대가)로 잡는다. 실제 홈택스 신고 기준이 공급가액(`과세`만)이어야 하면 이 한 줄만 조정.
    const taxable = num(row[C.taxable]) + num(row[C.vat]); // 공급대가
    const exempt = num(row[C.exempt]);
    if (taxable === 0 && exempt === 0) continue;

    const isCash = String(row[C.name] ?? '').includes('현금영수증');
    if (!byMonth.has(month)) byMonth.set(month, emptyRow(month));
    const mr = byMonth.get(month)!;
    const bucket = isCash ? mr.cashReceipt : mr.card;
    bucket.taxable += taxable;
    bucket.exempt += exempt;
    mr.taxable += taxable;
    mr.exempt += exempt;
  }

  const rows = Array.from(byMonth.values()).sort((a, b) => a.month.localeCompare(b.month));
  const rollup = rows.map(r => ({ month: r.month, taxable: r.taxable, exempt: r.exempt }));
  return { channel: '오프라인(이지체크)', rows, rollup };
}

export function parseEasyCheckFile(file: File, year: number, half: Half): Promise<ChannelResult<EasyCheckMonthRow>> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const aoa = sheetToAoaFullRange(ws);
        resolve(parseEasyCheckRows(aoa, year, half));
      } catch (err) { reject(err); }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}
